import { createContext, useContext, useMemo } from 'react';

import useLocalStorageSync from '@app/hooks/useLocalStorageSync';
import useResponsiveValue from '@app/hooks/useResponsiveValue';

export function useGNBFoldHandle() {
  const { folded, toggleFolded } = useContext(GNBFoldHandleContext);
  const isDesktopWidth = useResponsiveValue([false, false, true]);

  if (folded === undefined || toggleFolded === undefined) {
    throw new Error('GNBFoldHandleContext is not provided');
  }
  return useMemo(
    () => ({ folded: isDesktopWidth && folded, toggleFolded }),
    [folded, isDesktopWidth, toggleFolded]
  );
}

type FoldState = boolean;
type GNBFoldHandleContextValue = {
  folded: FoldState;
  toggleFolded: (nextValue?: FoldState) => void;
};

const GNBFoldHandleContext = createContext<GNBFoldHandleContextValue>({
  folded: false,
  toggleFolded: () => {},
});

export function GNBFoldHandleProvider({ children }: { children: React.ReactNode }) {
  const [folded = false, setFolded] = useLocalStorageSync<boolean>('modusign-GNB-fold', false);

  const contextValue = useMemo(
    () => ({
      folded,
      toggleFolded: (nextValue?: FoldState) =>
        nextValue !== undefined ? setFolded(nextValue) : setFolded(!folded),
    }),
    [folded, setFolded]
  );

  return (
    <GNBFoldHandleContext.Provider value={contextValue}>{children}</GNBFoldHandleContext.Provider>
  );
}
